import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { SingleValue, MultiValue } from "react-select";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import Loading from "../../../components/loading";
import StyledSelect from "../../../components/styled-select";
import ToastComponent from "../../../components/toast";
import { isSelectSingleValue } from "../../../core/utils";
import paths from "../../../navigation/paths";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  getAllCoursesState,
  CourseStatus,
  CourseLanguage,
  CourseLevel,
  allCoursesStateAction,
  CourseResponse,
} from "../../../redux/features/course-slice";
import { getUserState } from "../../../redux/features/auth-slice";
import { AppDispatch } from "../../../redux/store";
import { OptionValue } from "../../../types/component";
import Chip from "../../../components/chip";

export default function UserCoursesPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allCoursesState = useSelector(getAllCoursesState);
  const errorState = useSelector(getErrorState);
  const userState = useSelector(getUserState);
  const [selectedStatuses, setSelectedStatuses] = useState<CourseStatus[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<CourseLanguage[]>(
    []
  );
  const [selectedLevels, setSelectedLevels] = useState<CourseLevel[]>([]);

  const fetchAllCourses = useCallback(async () => {
    await dispatch(
      allCoursesStateAction({
        status: selectedStatuses,
        language: selectedLanguages,
        level: selectedLevels,
      })
    );
  }, [dispatch, selectedStatuses, selectedLanguages, selectedLevels]);

  useEffect(() => {
    fetchAllCourses();
  }, [fetchAllCourses]);

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "courses", path: null, title: "Browse Courses" },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (errorState) {
      toast(errorState.message, { type: "error", autoClose: 5000 });
    }
  }, [errorState]);

  // Filter out draft courses
  const displayedCourses = allCoursesState.data.filter(
    (course) => course.status !== CourseStatus.Draft
  );

  return (
    <>
      <Helmet title="Browse Courses" />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-end mb-6 gap-4">
          <StyledSelect
            options={Object.values(CourseStatus).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedStatuses.length > 0
                  ? `${selectedStatuses.length} status${
                      selectedStatuses.length > 1 ? "es" : ""
                    } selected`
                  : "All statuses",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedStatuses.includes(value.value as CourseStatus)) {
                  setSelectedStatuses(
                    selectedStatuses.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedStatuses([
                    ...selectedStatuses,
                    value.value as CourseStatus,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background: selectedStatuses.includes(value.value as CourseStatus)
                ? "#f1f5f9"
                : undefined,
            })}
            placeHolder="Select statuses"
          />
          <StyledSelect
            options={Object.values(CourseLanguage).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedLanguages.length > 0
                  ? `${selectedLanguages.length} language${
                      selectedLanguages.length > 1 ? "s" : ""
                    } selected`
                  : "All languages",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedLanguages.includes(value.value as CourseLanguage)) {
                  setSelectedLanguages(
                    selectedLanguages.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedLanguages([
                    ...selectedLanguages,
                    value.value as CourseLanguage,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background: selectedLanguages.includes(
                value.value as CourseLanguage
              )
                ? "#f1f5f9"
                : undefined,
            })}
            placeHolder="Select languages"
          />
          <StyledSelect
            options={Object.values(CourseLevel).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedLevels.length > 0
                  ? `${selectedLevels.length} level${
                      selectedLevels.length > 1 ? "s" : ""
                    } selected`
                  : "All levels",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedLevels.includes(value.value as CourseLevel)) {
                  setSelectedLevels(
                    selectedLevels.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedLevels([
                    ...selectedLevels,
                    value.value as CourseLevel,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background: selectedLevels.includes(value.value as CourseLevel)
                ? "#f1f5f9"
                : undefined,
            })}
            placeHolder="Select levels"
          />
        </div>

        {allCoursesState.loading ? (
          <div className="flex justify-center items-center h-64">
            <Loading />
          </div>
        ) : displayedCourses.length > 0 ? (
          <div className="w-full max-h-[calc(100vh-200px)] overflow-y-auto px-0 scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-gray-100 scrollbar-rounded">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
              {displayedCourses.map((course: CourseResponse) => {
                const isRegistered = userState.data?.id
                  ? course.registeredUsers?.some(
                      (user) => user.id === userState.data?.id
                    )
                  : false;
                return (
                  <div
                    key={course.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer relative"
                    onClick={() =>
                      navigate(`${paths.COURSES_USER}/${course.id}`)
                    }
                  >
                    <img
                      src={course.pictureUrl}
                      alt={course.name}
                      className="w-full h-40 object-cover rounded-t-lg"
                    />
                    <div className="p-4">
                      <div className="flex flex-row space-x-3">
                        {" "}
                        <h3 className="text-lg font-semibold text-gray-800 truncate">
                          {course.name}
                        </h3>
                        {isRegistered && (
                          <Chip
                            label={"Registered"}
                            type="success"
                            exClassName="w-fit"
                          />
                        )}
                        {course.status !== "active" && (
                          <Chip
                            label={"Unavailable"}
                            type="error"
                            exClassName="w-fit"
                          />
                        )}
                        {course.availability < 1 && (
                          <Chip
                            label={"Full"}
                            type="default"
                            exClassName="w-fit font-bold "
                          />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Authors:</span>{" "}
                        {course.authors
                          .map((author) => author.name)
                          .join(", ") || "No authors"}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 capitalize">
                        <span className="font-medium">Level:</span>{" "}
                        {course.level}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64">
            <EmptyDataOverlay label="No Courses Found" exClassName="h-20" />
          </div>
        )}
      </div>
      <ToastComponent id="userCourses" />
    </>
  );
}